import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, BarChart3, DollarSign, Layers, ArrowRight, ChevronRight } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="landing-page">
      {/* Nav */}
      <nav className="border-b border-zinc-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-primary p-1.5">
              <FolderOpen className="w-4 h-4 text-black" />
            </div>
            <span className="font-heading text-base font-bold uppercase tracking-wider text-zinc-900">Punchlist</span>
          </div>
          <button
            onClick={() => navigate('/login')}
            data-testid="landing-login-btn"
            className="text-xs font-heading font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            Log In
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,196,12,0.3) 39px, rgba(255,196,12,0.3) 40px),
              repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,196,12,0.3) 39px, rgba(255,196,12,0.3) 40px)`
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-32">
          <div className="inline-block bg-primary px-3 py-1 mb-6">
            <span className="text-[10px] font-heading font-bold uppercase tracking-[0.2em] text-black">For MEP Contractors</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-[1.1] mb-6 max-w-3xl">
            The Construction Procurement Operating System
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-xl mb-10 leading-relaxed">
            Track every project. Control every dollar. From PO to delivery, Punchlist gives MEP trade contractors full visibility over materials, budgets, and vendors.
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-3">
            <button
              onClick={() => navigate('/login?signup=true')}
              data-testid="landing-get-started-btn"
              className="w-full sm:w-auto px-8 py-3.5 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
            >
              Get Started Free <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/login')}
              data-testid="landing-hero-login-btn"
              className="w-full sm:w-auto px-8 py-3.5 bg-transparent text-zinc-400 font-heading font-semibold uppercase tracking-wider text-sm border border-zinc-700 hover:border-zinc-400 hover:text-white transition-colors"
            >
              I have an account
            </button>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: Layers,
                title: 'Track Every Project',
                desc: 'Phases, timelines, and team assignments in one place. Know exactly where every project stands from your phone.'
              },
              {
                icon: DollarSign,
                title: 'Control Your Budget',
                desc: 'Real-time spend tracking against budget. See what\'s ordered, shipped, and delivered with automatic cost rollups.'
              },
              {
                icon: BarChart3,
                title: 'See the Full Picture',
                desc: 'Vendor scorecards, price benchmarks, and predictive analytics. Make smarter procurement decisions.'
              }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-zinc-200 p-6 sm:p-8 hover:border-primary transition-colors group">
                <div className="w-10 h-10 bg-zinc-100 group-hover:bg-primary/10 flex items-center justify-center mb-5 transition-colors">
                  <item.icon className="w-5 h-5 text-zinc-600 group-hover:text-zinc-900" />
                </div>
                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-zinc-900 mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trades strip */}
      <section className="py-8 bg-zinc-950 border-y border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-heading font-semibold mb-3">Built for the trades that build everything else</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-zinc-500 font-mono text-xs tracking-wide">
            <span>PLUMBING</span><span className="text-primary">/</span>
            <span>ELECTRICAL</span><span className="text-primary">/</span>
            <span>HVAC</span><span className="text-primary">/</span>
            <span>FIRE PROTECTION</span><span className="text-primary">/</span>
            <span>MECHANICAL</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wide text-zinc-900 mb-3">Stop chasing POs. Start running projects.</h2>
          <p className="text-sm text-zinc-500 mb-8">Free to start. No credit card required.</p>
          <button
            onClick={() => navigate('/login?signup=true')}
            data-testid="landing-cta-btn"
            className="px-8 py-3.5 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm hover:bg-primary-hover transition-colors inline-flex items-center gap-2"
          >
            Create Your Account <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="bg-primary p-1">
              <FolderOpen className="w-3 h-3 text-black" />
            </div>
            <span className="font-heading text-xs font-bold uppercase tracking-wider text-zinc-700">Punchlist</span>
          </div>
          <p className="text-xs text-zinc-400">&copy; {new Date().getFullYear()} Punchlist. Built for contractors, by contractors.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
